/**
 * Фильтр точек карты по выбранному КАТО (иерархия из /api/public/kato/list).
 *
 * Правила:
 *  - ничего не выбрано / выбрана вся область (корень) → все точки;
 *  - выбран район → только точки его поддерева (сельские округа, СНП);
 *  - выбран СНП/округ → точки района-предка (клик по маркеру не должен
 *    прятать соседние точки района);
 *  - выбранный/неизвестный КАТО отсутствует в списке или список пуст
 *    (endpoint не ответил) → все точки, деградируем мягко.
 */
import type { KatoListItem } from '../requests/public.request';

/** КАТО всех узлов поддерева `root` (включая сам корень). */
export const subtreeKatos = (
  items: KatoListItem[],
  root: number,
): Set<number> => {
  const childrenByParent = new Map<number, number[]>();
  for (const item of items) {
    if (item.parent_id === null) continue;
    const siblings = childrenByParent.get(item.parent_id);
    if (siblings) {
      siblings.push(item.kato);
    } else {
      childrenByParent.set(item.parent_id, [item.kato]);
    }
  }

  const scope = new Set<number>();
  const stack: number[] = [root];
  while (stack.length > 0) {
    const kato = stack.pop();
    if (kato === undefined || scope.has(kato)) continue;
    scope.add(kato);
    for (const child of childrenByParent.get(kato) ?? []) {
      stack.push(child);
    }
  }
  return scope;
};

export const filterByKatoScope = <T extends { kato: number }>(
  points: T[],
  items: KatoListItem[],
  selectedKato: number | null,
): T[] => {
  if (selectedKato === null || items.length === 0) return points;

  const byKato = new Map(items.map((item) => [item.kato, item]));
  const selected = byKato.get(selectedKato);
  if (!selected) return points;
  // Корень (область) — без фильтра.
  if (selected.parent_id === null) return points;

  // Поднимаемся до района — прямого ребёнка корня.
  let district = selected;
  while (district.parent_id !== null) {
    const parent = byKato.get(district.parent_id);
    if (!parent || parent.parent_id === null) break;
    district = parent;
  }

  // Собираем все КАТО поддерева района.
  const childrenByParent = new Map<number, number[]>();
  for (const item of items) {
    if (item.parent_id === null) continue;
    const siblings = childrenByParent.get(item.parent_id);
    if (siblings) {
      siblings.push(item.kato);
    } else {
      childrenByParent.set(item.parent_id, [item.kato]);
    }
  }

  const scope = new Set<number>();
  const stack: number[] = [district.kato];
  while (stack.length > 0) {
    const kato = stack.pop();
    if (kato === undefined || scope.has(kato)) continue;
    scope.add(kato);
    for (const child of childrenByParent.get(kato) ?? []) {
      stack.push(child);
    }
  }

  return points.filter((point) => scope.has(point.kato));
};
